// zip でまとめてアップロードされた成果物を dashboard / data / details の 3 項目へ割り当てる。
// 展開はフロントだけで行い、送信は従来どおり multipart 3 パートのままにする（API 契約は不変）。
// セキュリティ境界は BE（backend/core/src/handlers/custom_dashboard/upload_validation/）のままで、
// ここでのサイズ・形式チェックは展開そのものを暴走させないためのガードと早期検出に過ぎない。

import { unzipSync, type UnzipFileInfo } from 'fflate';

import { DATA_MAX_BYTES, DETAILS_MAX_BYTES, HTML_MAX_BYTES } from './upload-validation';

/** zip 自体のサイズ上限。展開後の 3 ファイル上限合計（約 22.5 MB）に合わせる。 */
export const BUNDLE_MAX_BYTES = 23 * 1024 * 1024;
/** zip 内のファイル数上限。ダッシュボードは 3 ファイル構成のため十分に余裕がある。 */
export const BUNDLE_MAX_ENTRIES = 100;

export interface BundleEntry {
  file: File;
  text: string;
}

export interface DashboardBundle {
  dashboard: BundleEntry;
  data: BundleEntry;
  details: File | null;
}

export interface BundleResult {
  /** 3 項目を解決できた場合のみ非 null。errors が空でないときは必ず null。 */
  bundle: DashboardBundle | null;
  errors: string[];
}

interface Candidate {
  /** zip 内の実体名（展開時の filter 突合に使う）。 */
  name: string;
  /** 単一トップレベルフォルダを剥がした後の名前。 */
  relativeName: string;
  originalSize: number;
}

interface Slot {
  /** 正規化後のファイル名。BE には渡らないが取り込み結果の表示に使う。 */
  fileName: string;
  extensions: string[];
  maxBytes: number;
  mimeType: string;
}

const DASHBOARD_SLOT: Slot = {
  fileName: 'dashboard.html',
  extensions: ['.html', '.htm'],
  maxBytes: HTML_MAX_BYTES,
  mimeType: 'text/html',
};
const DATA_SLOT: Slot = {
  fileName: 'data.json',
  extensions: ['.json'],
  maxBytes: DATA_MAX_BYTES,
  mimeType: 'application/json',
};
const DETAILS_SLOT: Slot = {
  fileName: 'details.txt',
  extensions: ['.txt'],
  maxBytes: DETAILS_MAX_BYTES,
  mimeType: 'text/plain',
};

const ZIP_UNREADABLE = 'zip ファイルとして読み込めません';

/**
 * zip のバイト列から dashboard.html / data.json（任意で details.txt）を取り出す。
 * 利用者が一度に直せるよう、解決できなかった項目のエラーはまとめて返す。
 */
export function extractDashboardBundle(bytes: Uint8Array): BundleResult {
  if (bytes.length === 0 || bytes.length > BUNDLE_MAX_BYTES) {
    return { bundle: null, errors: [`zip は 1〜${BUNDLE_MAX_BYTES} bytes にしてください`] };
  }
  if (bytes[0] !== 0x50 || bytes[1] !== 0x4b) {
    return { bundle: null, errors: [ZIP_UNREADABLE] };
  }

  // 1 回目は filter で常に false を返し、展開せずにエントリ一覧だけを取る。
  // 展開後サイズ（originalSize）を展開前に判定でき、巨大な展開を始めずに済む。
  const infos: UnzipFileInfo[] = [];
  try {
    unzipSync(bytes, {
      filter: (info) => {
        infos.push(info);
        return false;
      },
    });
  } catch {
    return { bundle: null, errors: [ZIP_UNREADABLE] };
  }

  const candidates = toCandidates(infos);
  if (candidates.length > BUNDLE_MAX_ENTRIES) {
    return {
      bundle: null,
      errors: [`zip 内のファイルは ${BUNDLE_MAX_ENTRIES} 件以下にしてください`],
    };
  }

  const errors: string[] = [];
  const dashboard = resolveSlot(candidates, DASHBOARD_SLOT, true, errors);
  const data = resolveSlot(candidates, DATA_SLOT, true, errors);
  const details = resolveSlot(candidates, DETAILS_SLOT, false, errors);
  if (errors.length > 0) return { bundle: null, errors };

  const wanted = [dashboard, data, details].filter((c): c is Candidate => c !== null);
  let unzipped: Record<string, Uint8Array>;
  try {
    unzipped = unzipSync(bytes, { filter: (info) => wanted.some((c) => c.name === info.name) });
  } catch {
    return { bundle: null, errors: [ZIP_UNREADABLE] };
  }

  const dashboardEntry = toEntry(unzipped, dashboard, DASHBOARD_SLOT, errors);
  const dataEntry = toEntry(unzipped, data, DATA_SLOT, errors);
  if (errors.length > 0 || dashboardEntry === null || dataEntry === null) {
    return { bundle: null, errors: errors.length > 0 ? errors : [ZIP_UNREADABLE] };
  }
  return {
    bundle: {
      dashboard: dashboardEntry,
      data: dataEntry,
      details: details === null ? null : toFile(unzipped[details.name], DETAILS_SLOT),
    },
    errors: [],
  };
}

/** ディレクトリ・OS が付ける副産物を落とし、単一トップレベルフォルダを剥がす。 */
function toCandidates(infos: UnzipFileInfo[]): Candidate[] {
  const files = infos.filter((info) => !isIgnored(info.name));
  const topLevels = new Set(
    files.map((info) =>
      info.name.includes('/') ? info.name.slice(0, info.name.indexOf('/')) : '',
    ),
  );
  // Finder の「圧縮」はフォルダを 1 階層包むため、全ファイルが同じフォルダ配下なら剥がす。
  const prefix =
    files.length > 0 && topLevels.size === 1 && !topLevels.has('') ? `${[...topLevels][0]}/` : '';
  return files.map((info) => ({
    name: info.name,
    relativeName: info.name.slice(prefix.length),
    originalSize: info.originalSize,
  }));
}

function isIgnored(name: string): boolean {
  if (name.endsWith('/')) return true; // ディレクトリエントリ
  const segments = name.split('/');
  if (segments.includes('__MACOSX')) return true;
  return segments[segments.length - 1].startsWith('.'); // .DS_Store など
}

/**
 * 固定名（dashboard.html 等）を優先し、無ければ拡張子が一意に定まる場合だけ採用する。
 * サブフォルダ配下は対象にしない（成果物は 3 ファイルのフラット構成）。
 */
function resolveSlot(
  candidates: Candidate[],
  slot: Slot,
  required: boolean,
  errors: string[],
): Candidate | null {
  const flat = candidates.filter((c) => !c.relativeName.includes('/'));
  const exact = flat.find((c) => c.relativeName.toLowerCase() === slot.fileName);
  const matched = exact ?? pickUnique(flat, slot, errors) ?? null;
  if (matched === null) {
    if (required && !flat.some((c) => hasExtension(c, slot))) {
      errors.push(`zip 内に ${slot.fileName}（${slot.extensions[0]}）が見つかりません`);
    }
    return null;
  }
  if (matched.originalSize > slot.maxBytes) {
    errors.push(`zip 内の ${slot.fileName} は展開後 ${slot.maxBytes} bytes 以下にしてください`);
    return null;
  }
  return matched;
}

function pickUnique(flat: Candidate[], slot: Slot, errors: string[]): Candidate | null {
  const matches = flat.filter((c) => hasExtension(c, slot));
  if (matches.length === 1) return matches[0];
  if (matches.length > 1) {
    errors.push(
      `zip 内に ${slot.extensions[0]} が複数あります。${slot.fileName} という名前にしてください: ${matches
        .map((c) => c.relativeName)
        .join(', ')}`,
    );
  }
  return null;
}

function hasExtension(candidate: Candidate, slot: Slot): boolean {
  const name = candidate.relativeName.toLowerCase();
  return slot.extensions.some((extension) => name.endsWith(extension));
}

function toEntry(
  unzipped: Record<string, Uint8Array>,
  candidate: Candidate | null,
  slot: Slot,
  errors: string[],
): BundleEntry | null {
  if (candidate === null) return null;
  const bytes = unzipped[candidate.name];
  if (bytes === undefined) {
    errors.push(ZIP_UNREADABLE);
    return null;
  }
  let text: string;
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    errors.push(`zip 内の ${slot.fileName} は UTF-8 である必要があります`);
    return null;
  }
  return { file: toFile(bytes, slot), text };
}

function toFile(bytes: Uint8Array, slot: Slot): File {
  // TS 5.7 以降の Uint8Array は ArrayBufferLike 上の view 型で BlobPart と一致しないため、
  // 実体を ArrayBuffer にコピーしてから File を作る。
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return new File([buffer], slot.fileName, { type: slot.mimeType });
}
